import React from 'react';
import { AlertTriangle, Flag, Pause, Loader2 } from 'lucide-react';

// iRacing SessionFlags bitfield — yellow (0x0008) and full-course caution
// (0x4000) both drive the pulsing indicator. Per pyirsdk Flags class.
const YELLOW_FLAG_BITS = 0x0008 | 0x4000;

// ---------------------------------------------------------------------------
// Contextual rig state derivation
// ---------------------------------------------------------------------------
// Five states, derived from how recently a packet arrived, so the badge always
// agrees with what the card body beneath it is showing:
//   live    — green, pulsing dot, "LIVE" (packet within ~6s)
//   in_pits — amber dot, "IN PITS" (explicit flag from the sim, not a timeout)
//   paused  — slate pause icon, "PAUSED"
//   stale   — amber dot, "STALE" (recently silent, ~6–60s: the body is still
//             showing the last telemetry it received)
//   standby — grey dot, "STANDBY" (no packet yet, or silent past ~60s — the
//             same moment the card body falls back to its placeholder)
//
// Priority: standby (timeout) > paused > in_pits > stale > live

export function deriveRigState(rig) {
  if (!rig) return 'standby';
  const last = rig.last_update ? new Date(rig.last_update).getTime() : 0;
  const live = last && (Date.now() - last < 6000);
  const standby = !last || (Date.now() - last >= 60000);

  if (standby) return 'standby';
  if (rig.is_paused) return 'paused';
  if (rig.in_pits) return 'in_pits';
  if (!live) return 'stale';
  return 'live';
}

export const RIG_STATE_BADGE = {
  live:    { label: 'LIVE',    dotClass: 'bg-emerald-400', textClass: 'text-emerald-400',  ping: true,  icon: null  },
  in_pits: { label: 'IN PITS', dotClass: 'bg-amber-400',   textClass: 'text-amber-400/80', ping: false, icon: null  },
  paused:  { label: 'PAUSED',  dotClass: 'bg-slate-400',   textClass: 'text-slate-400',    ping: false, icon: 'pause' },
  stale:   { label: 'STALE',   dotClass: 'bg-amber-400',   textClass: 'text-amber-400/80', ping: false, icon: null  },
  standby: { label: 'STANDBY', dotClass: 'bg-white/20',    textClass: 'text-white/30',     ping: false, icon: null  },
};

// Reusable status badge — dot + label or pause icon + label.
// `variant="pill"` wraps in a bordered box (featured cards);
// `variant="plain"` is just the inline dot+text (compact cards / detail header).
export function RigStatusBadge({ rig, size = 'sm', variant = 'plain', accent = null }) {
  const state = deriveRigState(rig);
  const badge = RIG_STATE_BADGE[state];
  const dot = size === 'lg' ? 'w-2 h-2' : 'w-1.5 h-1.5';
  const txt = size === 'lg' ? 'text-[10px]' : 'text-[9px]';

  const dotEl = badge.icon === 'pause'
    ? <Pause className={size === 'lg' ? 'w-3 h-3' : 'w-2.5 h-2.5'} />
    : (
      <span className={`relative flex ${dot}`}>
        {badge.ping && <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${badge.dotClass}`} />}
        <span className={`relative inline-flex rounded-full h-full w-full ${badge.dotClass}`} />
      </span>
    );

  // In pill variant, live state uses the accent color if provided (Porsche/AMG themes)
  const liveText = accent && state === 'live' ? '' : badge.textClass;
  const pillCls = variant === 'pill'
    ? `border ${state === 'live' && accent ? 'border-white/20 bg-white/5' : (state === 'in_pits' || state === 'stale') ? 'border-amber-400/30 bg-amber-400/10' : state === 'paused' ? 'border-slate-400/30 bg-slate-400/10' : 'border-white/15 bg-white/5'}`
    : '';
  const pillPad = variant === 'pill' ? (size === 'lg' ? 'px-2.5 py-1.5' : 'px-2 py-1') : '';

  return (
    <span className={`inline-flex items-center gap-1.5 font-bold tracking-widest uppercase ${txt} ${liveText || badge.textClass} ${pillCls} ${pillPad}`}>
      {dotEl}
      {badge.label}
    </span>
  );
}

export function isShiftLight(rig) {
  if (!rig) return false;
  if (rig.shift_indicator_pct != null && Number(rig.shift_indicator_pct) >= 0.90) return true;
  if (rig.rpm && rig.max_rpm && (Number(rig.rpm) / Number(rig.max_rpm)) >= 0.95) return true;
  return false;
}

export function isCaution(sessionFlags) {
  return sessionFlags != null && (Number(sessionFlags) & YELLOW_FLAG_BITS) !== 0;
}

// Live lap-delta badge: emerald + pulsing border when negative (faster),
// brand red + pulsing border when positive (slower). Hidden when null/zero.
export function LapDeltaBadge({ delta, size = 'sm', variant = 'badge' }) {
  if (delta == null || delta === 0) return null;
  const negative = delta < 0;
  const abs = Math.abs(delta).toFixed(3);

  // Inline variant — plain bracketed coloured text, no border/pulse.
  if (variant === 'inline') {
    return (
      <span className={`font-mono font-bold tabular-nums ${negative ? 'text-emerald-400' : 'text-[#dd1f26]'}`}>
        [{negative ? '-' : '+'}{abs}s]
      </span>
    );
  }

  const pulse = negative ? 'animate-delta-green' : 'animate-delta-red';
  const tone = negative
    ? 'text-emerald-300 border-emerald-400/40 bg-emerald-400/10'
    : 'text-[#dd1f26] border-[#dd1f26]/40 bg-[#dd1f26]/10';
  const sz = size === 'lg' ? 'text-sm px-3 py-1.5' : 'text-[11px] px-2 py-1';
  return (
    <span className={`inline-flex items-center font-mono font-bold tabular-nums border ${pulse} ${tone} ${sz}`}>
      {negative ? '-' : '+'}{abs}s
    </span>
  );
}

// Linear track-position bar (circuit ribbon 0-100%) with a glowing dot.
export function TrackPositionBar({ pct, accent = '#dd1f26', size = 'sm' }) {
  const pos = pct != null ? Math.max(0, Math.min(100, Number(pct) * 100)) : 0;
  const h = size === 'lg' ? 'h-2.5' : 'h-1.5';
  const dot = size === 'lg' ? 'w-3 h-3' : 'w-2 h-2';
  const half = size === 'lg' ? 6 : 4;
  return (
    <div className={`relative w-full ${h} bg-white/5 overflow-visible`}>
      <div className="absolute inset-y-0 left-0 opacity-15" style={{ width: `${pos}%`, background: accent }} />
      <div
        className={`absolute top-1/2 ${dot} -translate-y-1/2 rounded-full track-dot-glow telemetry-dot`}
        style={{ left: `calc(${pos}% - ${half}px)`, background: accent }}
      />
    </div>
  );
}

// Pulsing yellow caution indicator when the session_flags yellow bit is set.
export function FlagCautionIndicator({ sessionFlags, size = 'sm' }) {
  if (!isCaution(sessionFlags)) return null;
  const sz = size === 'lg' ? 'px-3 py-1.5 text-xs' : 'px-2 py-0.5 text-[10px]';
  const ic = size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  return (
    <span className={`inline-flex items-center gap-1.5 font-bold tracking-widest uppercase border border-amber-400/50 bg-amber-400/15 text-amber-400 animate-caution-pulse ${sz}`}>
      <Flag className={ic} /> Caution
    </span>
  );
}

// Subtle incidents counter pill.
export function IncidentPill({ incidents, size = 'sm' }) {
  if (incidents == null) return null;
  const sz = size === 'lg' ? 'text-xs px-2.5 py-1' : 'text-[10px] px-2 py-0.5';
  const ic = size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  return (
    <span className={`inline-flex items-center gap-1 font-bold tracking-wider uppercase border border-white/10 bg-white/5 text-white/60 ${sz}`}>
      <AlertTriangle className={ic} /> Incidents {incidents}x
    </span>
  );
}

// ---------------------------------------------------------------------------
// Cold-start guard
// ---------------------------------------------------------------------------
// A sim publishes its session metadata a moment after it starts reporting: the
// opening iRacing frames arrive with a null track name and a zero session time.
// A rig that is reporting but has neither session metadata nor any telemetry yet
// is therefore still spinning up, and holds a muted placeholder instead of
// rendering an empty live cockpit full of dashes and zeros. The moment a real
// value lands the placeholder clears.
export function isSessionInitialising(rig) {
  if (!rig) return false;
  if (deriveRigState(rig) === 'standby') return false; // standby has its own placeholder
  const hasText = (v) => typeof v === 'string' && v.trim() !== '';
  const noSession = !hasText(rig.track) && !hasText(rig.car);
  const noTelemetry =
    !(Number(rig.speed_kph) > 0) &&
    rig.rpm == null &&
    !hasText(rig.lap_time) &&
    !hasText(rig.best_lap_time);
  return noSession && noTelemetry;
}

// Muted, non-alarming placeholder shown while a reporting rig waits for its
// first valid session frame. Deliberately distinct from the STANDBY card (no
// packet at all) and from a genuinely idle or stale rig.
export function GridInitialisingPlaceholder({ size = 'sm', className = '' }) {
  const pad = size === 'lg' ? 'py-16' : 'py-10';
  const ic = size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  return (
    <div className={`px-5 ${pad} flex flex-col items-center justify-center gap-2 ${className}`}>
      <Loader2 className={`${ic} text-white/15 animate-spin`} />
      <p className="text-white/30 text-xs font-bold tracking-widest uppercase">Grid Initialising…</p>
      <p className="text-white/20 text-[10px] font-medium">Awaiting Session Data</p>
    </div>
  );
}